import { dealFilterDesc, isLogicalFilter, useDataGrid } from 'admin';
import type { InspectionDTO } from 'api';
import type { Dayjs } from 'date';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DataGrid, Page, Section } from 'ui';

import { createFromFilter, createToFilter, TimeFilter } from '~/time-filter';

import { columns } from './columns';
import { TIME_FILTER_FIELD_NAME } from './constants';

export const InspectionsList = () => {
  const navigate = useNavigate();

  const { dataGridProps, setFilters, filters } = useDataGrid({
    filters: {
      mode: 'server',
    },
    resource: '/inspections/v2/',
    sorters: {
      initial: [dealFilterDesc],
      mode: 'server',
    },
    syncWithLocation: false,
  });

  const nonTimeFilters = useMemo(
    () =>
      filters.filter((filter) => {
        if (!isLogicalFilter(filter)) {
          return true;
        }
        return filter.field !== TIME_FILTER_FIELD_NAME;
      }),
    [filters]
  );

  const handleTimeFilterChange = useCallback(
    ({ from, to }: { from: Dayjs; to: Dayjs }) => {
      setFilters([
        ...nonTimeFilters,
        createFromFilter(from, TIME_FILTER_FIELD_NAME),
        createToFilter(to, TIME_FILTER_FIELD_NAME),
      ]);
    },
    [setFilters, nonTimeFilters]
  );

  return (
    <Page>
      <Section
        title="Inspections"
        action={
          <Button size="medium" component={Link} to="/ground-control/inspections/create">
            Create inspection
          </Button>
        }
      >
        <TimeFilter onDateChange={handleTimeFilterChange} />
        <DataGrid<InspectionDTO>
          {...dataGridProps}
          showColumnVerticalBorder
          showCellVerticalBorder
          autoHeight
          filterDebounceMs={500}
          density="standard"
          sx={{ overflowX: 'scroll' }}
          columns={columns}
          onRowClick={(params) => {
            navigate(`/ground-control/inspections/${params.row.id}`);
          }}
        />
      </Section>
    </Page>
  );
};
